import useAuth from "./hooks/useAuth";
import { useEffect, useState } from "react";
import StackedBarChart from "./StackedBarChart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Custom hook to detect screen size
const useScreenSize = () => {
    const [isLargeScreen, setIsLargeScreen] = useState(false);

    useEffect(() => {
        const checkScreenSize = () => {
            setIsLargeScreen(window.innerWidth >= 768);
        };

        checkScreenSize(); // Initial check
        window.addEventListener('resize', checkScreenSize);
        
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    return isLargeScreen;
};

const CheckUserPhonemesDynamic = ({ language, date, checkuser }) => {
    const { auth, setAuth } = useAuth();
    const [dynamic, setDynamic] = useState();
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [phonemeGroups, setPhonemeGroups] = useState([]);
    const isLargeScreen = useScreenSize();

    useEffect(() => { 
        if (language) {
            getMyDynamics();
        }
    }, [language, date]);

    useEffect(() => {
        if (dynamic?.phonemes) {
            // Group phonemes into chunks of approximately 8-12 phonemes per page
            const groupSize = Math.ceil(dynamic.phonemes.length / 4);
            const groups = [];
            for (let i = 0; i < dynamic.phonemes.length; i += groupSize) {
                groups.push(dynamic.phonemes.slice(i, i + groupSize));
            }
            setPhonemeGroups(groups);
            setCurrentPage(1);
        }
    }, [dynamic]);

    const getMyDynamics = async () => {
        const token = localStorage.getItem("token");
        setLoading(true);
        
        try {
            let query = `${process.env.REACT_APP_USER_API_URL}/analysis/${language}/phonemes/dynamics`;
            if (checkuser) {
                query = `${process.env.REACT_APP_USER_API_URL}/analysis/${language}/phonemes/dynamics?checkuser=${checkuser}`;
            }

            const response = await fetch(query, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const json = await response.json();
                setDynamic(json);
            } else if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
            }
        } catch (error) {
            console.error("Error fetching phoneme dynamics:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const getCurrentPhonemes = () => {
        return phonemeGroups[currentPage - 1] || [];
    };

    const getPageLabel = (pageIndex) => {
        const phonemes = phonemeGroups[pageIndex] || [];
        if (phonemes.length === 0) return `Page ${pageIndex + 1}`;
        
        if (phonemes.length <= 3) {
            return phonemes.join(', ');
        } else {
            return `${phonemes[0]}, ${phonemes[1]}, ... +${phonemes.length - 2} more`;
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12 w-full h-full">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-2">Loading phoneme statistics...</p>
                </div>
            </div>
        );
    }

    if (!dynamic) {
        return (
            <div className="text-center py-12 w-full h-full">
                <div className="text-gray-400 text-4xl mb-2">📊</div>
                <p className="text-gray-500">No phoneme data available</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full max-w-none space-y-8 p-4">
            {/* Header */}
            <div className="text-center">
                <h2 className="text-xl sm:text-2xl font-bold text-blue-700">Phonemes Analysis</h2>
                <p className="text-gray-600 mt-2 text-sm sm:text-base">
                    Viewing pronunciation progress for {checkuser || "user"}
                </p>
            </div>

            {/* Main Phoneme Chart - Performance Groups with Sub-Blocks */}
            {dynamic.phonemes.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 w-full">
                    <div className="text-center mb-2">
                        <h3 className="text-lg font-semibold text-gray-800">Phoneme Performance Levels</h3>
                        <p className="text-gray-600 text-sm mt-1">Grouped by pronunciation accuracy</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {/* Excellent (80-100%) */}
                        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-4 h-4 bg-[#10B981] rounded-full"></div>
                                <div>
                                    <h4 className="font-semibold text-green-800">Excellent</h4>
                                    <p className="text-xs text-green-600">80-100%</p>
                                </div>
                            </div>
                            
                            {/* Native-like Sub-block */}
                            <div className="mb-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-3 h-3 bg-[#059669] rounded-full"></div>
                                    <span className="text-sm font-medium text-green-700">Native-like (90-100%)</span>
                                </div>
                                <div className="flex flex-wrap gap-2 min-h-[40px]">
                                    {dynamic.weighted_phonemes
                                        .filter(p => p.weight >= 90)
                                        .map(phoneme => (
                                        <span key={phoneme.phoneme} className="bg-white px-2 py-1 rounded-full border border-green-300 text-green-700 font-medium text-xs">
                                            {phoneme.phoneme}
                                        </span>
                                    ))}
                                    {dynamic.weighted_phonemes.filter(p => p.weight >= 90).length === 0 && (
                                        <span className="text-green-600 text-xs italic">None</span>
                                    )}
                                </div>
                            </div>
                            
                            {/* Good Sub-block */}
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-3 h-3 bg-[#10B981] rounded-full"></div>
                                    <span className="text-sm font-medium text-green-700">Excellent (80-89%)</span>
                                </div>
                                <div className="flex flex-wrap gap-2 min-h-[40px]">
                                    {dynamic.weighted_phonemes
                                        .filter(p => p.weight >= 80 && p.weight < 90)
                                        .map(phoneme => (
                                        <span key={phoneme.phoneme} className="bg-white px-2 py-1 rounded-full border border-green-300 text-green-700 font-medium text-xs">
                                            {phoneme.phoneme}
                                        </span>
                                    ))}
                                    {dynamic.weighted_phonemes.filter(p => p.weight >= 80 && p.weight < 90).length === 0 && (
                                        <span className="text-green-600 text-xs italic">None</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Good (40-80%) */}
                        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-4 h-4 bg-[#F59E0B] rounded-full"></div>
                                <div>
                                    <h4 className="font-semibold text-amber-800">Good</h4>
                                    <p className="text-xs text-amber-600">40-80%</p>
                                </div>
                            </div>
                            
                            {/* Solid Sub-block */}
                            <div className="mb-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-3 h-3 bg-[#D97706] rounded-full"></div>
                                    <span className="text-sm font-medium text-amber-700">Solid (60-79%)</span>
                                </div>
                                <div className="flex flex-wrap gap-2 min-h-[40px]">
                                    {dynamic.weighted_phonemes
                                        .filter(p => p.weight >= 60 && p.weight < 80)
                                        .map(phoneme => (
                                        <span key={phoneme.phoneme} className="bg-white px-2 py-1 rounded-full border border-amber-300 text-amber-700 font-medium text-xs">
                                            {phoneme.phoneme}
                                        </span>
                                    ))}
                                    {dynamic.weighted_phonemes.filter(p => p.weight >= 60 && p.weight < 80).length === 0 && (
                                        <span className="text-amber-600 text-xs italic">None</span>
                                    )}
                                </div>
                            </div>
                            
                            {/* Basic Sub-block */}
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-3 h-3 bg-[#FBBF24] rounded-full"></div>
                                    <span className="text-sm font-medium text-amber-700">Basic (40-59%)</span>
                                </div>
                                <div className="flex flex-wrap gap-2 min-h-[40px]">
                                    {dynamic.weighted_phonemes
                                        .filter(p => p.weight >= 40 && p.weight < 60)
                                        .map(phoneme => (
                                        <span key={phoneme.phoneme} className="bg-white px-2 py-1 rounded-full border border-amber-200 text-amber-600 font-medium text-xs">
                                            {phoneme.phoneme}
                                        </span>
                                    ))}
                                    {dynamic.weighted_phonemes.filter(p => p.weight >= 40 && p.weight < 60).length === 0 && (
                                        <span className="text-amber-500 text-xs italic">None</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Needs Practice (0-40%) */}
                        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-4 h-4 bg-[#EF4444] rounded-full"></div>
                                <div>
                                    <h4 className="font-semibold text-red-800">Needs Practice</h4>
                                    <p className="text-xs text-red-600">0-40%</p>
                                </div>
                            </div>
                            
                            {/* Developing Sub-block */}
                            <div className="mb-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-3 h-3 bg-[#DC2626] rounded-full"></div>
                                    <span className="text-sm font-medium text-red-700">Developing (20-39%)</span>
                                </div>
                                <div className="flex flex-wrap gap-2 min-h-[40px]">
                                    {dynamic.weighted_phonemes
                                        .filter(p => p.weight >= 20 && p.weight < 40)
                                        .map(phoneme => (
                                        <span key={phoneme.phoneme} className="bg-white px-2 py-1 rounded-full border border-red-300 text-red-700 font-medium text-xs">
                                            {phoneme.phoneme}
                                        </span>
                                    ))}
                                    {dynamic.weighted_phonemes.filter(p => p.weight >= 20 && p.weight < 40).length === 0 && (
                                        <span className="text-red-600 text-xs italic">None</span>
                                    )}
                                </div>
                            </div>
                            
                            {/* Beginner Sub-block */}
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-3 h-3 bg-[#EF4444] rounded-full"></div>
                                    <span className="text-sm font-medium text-red-700">Beginner (0-19%)</span>
                                </div>
                                <div className="flex flex-wrap gap-2 min-h-[40px]">
                                    {dynamic.weighted_phonemes
                                        .filter(p => p.weight < 20)
                                        .map(phoneme => (
                                        <span key={phoneme.phoneme} className="bg-white px-2 py-1 rounded-full border border-red-300 text-red-700 font-medium text-xs">
                                            {phoneme.phoneme}
                                        </span>
                                    ))}
                                    {dynamic.weighted_phonemes.filter(p => p.weight < 20).length === 0 && (
                                        <span className="text-red-600 text-xs italic">None</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary Statistics - Updated */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
                            <div>
                                <div className="text-2xl font-bold text-red-600">
                                    {dynamic.weighted_phonemes.filter(p => p.weight < 40).length}
                                </div>
                                <div className="text-sm text-gray-600">Need Practice</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-yellow-600">
                                    {dynamic.weighted_phonemes.filter(p => p.weight >= 40 && p.weight < 80).length}
                                </div>
                                <div className="text-sm text-gray-600">Good</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-green-600">
                                    {dynamic.weighted_phonemes.filter(p => p.weight >= 80).length}
                                </div>
                                <div className="text-sm text-gray-600">Excellent</div>
                            </div>
                        </div>
                        
                        {/* Practice Recommendation */}
                        {dynamic.weighted_phonemes.filter(p => p.weight < 40).length > 0 && (
                            <div className="mt-4 p-3 bg-yellow-50 rounded border border-yellow-200">
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <p className="text-sm text-yellow-800">
                                        Focus on practicing the <strong>{dynamic.weighted_phonemes.filter(p => p.weight < 40).length} phonemes</strong> in the "Needs Practice" category
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Celebration for Excellent */}
                        {dynamic.weighted_phonemes.filter(p => p.weight >= 80).length > 0 && (
                            <div className="mt-3 p-3 bg-green-50 rounded border border-green-200">
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-sm text-green-800">
                                        Great work! You have <strong>{dynamic.weighted_phonemes.filter(p => p.weight >= 80).length} phonemes</strong> at excellent proficiency
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Pagination Controls */}
            {phonemeGroups.length > 1 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="text-sm text-gray-600">
                            Showing group {currentPage} of {phonemeGroups.length}
                            {getCurrentPhonemes().length > 0 && (
                                <span className="text-blue-600 ml-2">
                                    ({getCurrentPhonemes().length} phonemes)
                                </span>
                            )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Previous
                            </button>
                            
                            <div className="flex gap-1">
                                {phonemeGroups.map((_, index) => {
                                    const page = index + 1;
                                    // Show first 2 pages, last 2 pages, and current page with neighbors
                                    if (
                                        page === 1 || 
                                        page === phonemeGroups.length || 
                                        (page >= currentPage - 1 && page <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => handlePageChange(page)}
                                                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors min-w-[100px] ${
                                                    currentPage === page
                                                        ? 'bg-blue-600 text-white border border-blue-600'
                                                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                }`}
                                                title={getPageLabel(index)}
                                            >
                                                {getPageLabel(index)}
                                            </button>
                                        );
                                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                                        return <span key={page} className="px-2 py-2 text-gray-500">...</span>;
                                    }
                                    return null;
                                })}
                            </div>
                            
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === phonemeGroups.length}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>

                    {/* Current Group Display */}
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                            <span className="font-semibold text-blue-800">Current Group:</span>
                            <div className="flex flex-wrap gap-1">
                                {getCurrentPhonemes().map((phoneme, index) => (
                                    <span 
                                        key={phoneme}
                                        className="bg-white px-2 py-1 rounded-full border border-blue-200 text-blue-700 text-xs font-medium"
                                    >
                                        {phoneme}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Individual Phoneme Charts - Full Width Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 w-full">
                {getCurrentPhonemes().map((key, i) => {
                    let data_inside = null;
                    let data_end = null;
                    let data_start = null;
                    let sample_inside = "";
                    let sample_start = "";
                    let sample_end = "";
                    let sample = "";
                    let data_av = null;
                    let data_av_start = null;
                    let data_av_end = null;
                    let data_av_inside = null;

                    if (dynamic.full.hasOwnProperty(key)) {
                        data_av = dynamic.full[key];
                    }
                    
                    if (dynamic.dynamic.hasOwnProperty(key)) {  
                        sample = dynamic.dynamic[key][0][0].sample;
                        
                        if (dynamic.full.hasOwnProperty(key + "_start")) {
                            data_av_start = dynamic.full[key + '_start'];
                        }
                        if (dynamic.full.hasOwnProperty(key + "_end")) {
                            data_av_end = dynamic.full[key + '_end'];
                        }
                        if (dynamic.full.hasOwnProperty(key + "_inside")) {
                            data_av_inside = dynamic.full[key + '_inside'];
                        }
                        
                        if (dynamic.dynamic.hasOwnProperty(key + "_start")) {
                            data_start = dynamic.dynamic[key + '_start'];
                            sample_start = dynamic.dynamic[key + '_start'][0][0].sample;
                        }
                        if (dynamic.dynamic.hasOwnProperty(key + "_inside")) {
                            data_inside = dynamic.dynamic[key + '_inside'];
                            sample_inside = dynamic.dynamic[key + '_inside'][0][0].sample;
                        }
                        if (dynamic.dynamic.hasOwnProperty(key + "_end")) {
                            data_end = dynamic.dynamic[key + '_end'];
                            sample_end = dynamic.dynamic[key + '_end'][0][0].sample;
                        }

                        return (
                            <div 
                                key={key + i} 
                                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow duration-200 min-h-[400px] flex flex-col w-full"
                            >
                                <StackedBarChart  
                                    phoneme={key} 
                                    data_av={data_av} 
                                    data_av_end={data_av_end} 
                                    data_av_start={data_av_start} 
                                    data_av_inside={data_av_inside} 
                                    data={dynamic.dynamic[key]} 
                                    data_end={data_end} 
                                    data_start={data_start} 
                                    data_inside={data_inside} 
                                    sample={sample} 
                                    sample_end={sample_end} 
                                    sample_start={sample_start} 
                                    sample_inside={sample_inside}
                                />
                            </div>
                        );
                    }
                    return null;
                })}
            </div>

            {/* Color Code Legend */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 w-full">
                <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Color Code Guide</h3>
                    <p className="text-gray-600 text-sm mt-1">Understanding the performance indicators</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="w-4 h-4 bg-[#10B981] rounded-full flex-shrink-0"></div>
                        <div>
                            <div className="font-semibold text-green-800">80-100% (Excellent)</div>
                            <div className="text-xs text-green-600">Consistently accurate pronunciation</div>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="w-4 h-4 bg-[#F59E0B] rounded-full flex-shrink-0"></div>
                        <div>
                            <div className="font-semibold text-yellow-800">40-80% (Good)</div>
                            <div className="text-xs text-yellow-600">Generally correct with some variations</div>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="w-4 h-4 bg-[#EF4444] rounded-full flex-shrink-0"></div>
                        <div>
                            <div className="font-semibold text-red-800">0-40% (Needs Practice)</div>
                            <div className="text-xs text-red-600">Requires focused practice and improvement</div>
                        </div>
                    </div>
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="text-sm text-blue-800">
                            <p className="font-medium">How to read the charts:</p>
                            <p className="mt-1">Each phoneme chart shows your performance over time. The colored bars represent your accuracy levels - aim for more green (excellent) and less red (needs practice).</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Summary - Full Width */}
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 sm:p-6 w-full">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">Data Summary</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm w-full">
                    <div className="text-center p-3 bg-white rounded-lg border">
                        <p className="text-blue-600 font-semibold">Total Phonemes</p>
                        <p className="text-2xl font-bold text-gray-800">{dynamic.phonemes.length}</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                        <p className="text-blue-600 font-semibold">Language</p>
                        <p className="text-lg font-semibold text-gray-800">{language}</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                        <p className="text-blue-600 font-semibold">Time Period</p>
                        <p className="text-lg font-semibold text-gray-800">{date}</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                        <p className="text-blue-600 font-semibold">User</p>
                        <p className="text-lg font-semibold text-gray-800">{checkuser || "Current"}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CheckUserPhonemesDynamic;